import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useBlocker, useNavigate } from 'react-router';
import { isEmpty, includes, isFunction } from 'lodash';

import multiSelect from 'component-utilities/multiSelect/MultiSelect';

import routeLeaveHook from '../../global/lib/routeLeaveHook';
import fixedHeightContent from '../../global/lib/fixedHeightContent';
import Loading from '../../global/components/Loading';

import IfFeature from '../../global/components/IfFeature';

import { updateDropdown } from '../../global/state_managers/ExportTableUIStateManager';
import onDemandPurgeEnabledForAccount from '../../global/lib/onDemandPurgeEnabledForAccount';


import withSerialResultsLoader, { LOAD_TYPES } from '../../global/lib/withSerialResultsLoader';
import LoadWhenReady from '../../global/lib/LoadWhenReady';
import FlashMessages, {
  flashConnect,
  PAGES,
} from '../../global/components/FlashMessages';
import { openSavedSearchStream } from '../../global/lib/searchResultsStreamManager';
import {
  searchRelatedJobs,
  resetReviewJob,
  resetHoldReasonsjob,
  isHoldReasonsJobRunning,
} from '../../global/state_managers/BulkActionsStateManager';
import { loadSearchLists } from '../../global/state_managers/SearchListsStateManager';
import { resetAssetStore } from '../../global/state_managers/AssetStateManager';
import {
  resetFlash,
  showGeneralFlash,
  showSuccessFlash,
  showFailureFlash,
} from '../../global/state_managers/FlashMessagesStateManager';
import { breadcrumbsActions } from '../../global/state_managers/BreadcrumbsStateManager';
import {
  changeAppView,
  resetResults,
} from '../../global/state_managers/SearchResultsStateManager';
import { initTableConfig } from '../../global/state_managers/SearchResults/tableConfig';
import { resetStream } from '../../global/state_managers/SearchResults/searchResultsStream';
import {
  loadSavedSearch,
  resetCurrentSavedSearch,
} from '../../global/state_managers/CurrentSavedSearchStateManager';

import {
  stateLogic,
  isReady,
  isSplitView,
} from './SearchResults/SearchResultsDeconstructed';

const SearchResultsSplitView = React.lazy(() => import('./SearchResults/SearchResultsSplitView'));
const Loaded = React.lazy(() => import('./SearchResults/Loaded'));

const VALID_FLASH_VIEWS = [PAGES.SEARCH_RESULTS];

// Apply all HOCs first
@routeLeaveHook
@multiSelect
@flashConnect(PAGES.SEARCH_RESULTS, 'SearchResults/componentWillUnmount')
@withSerialResultsLoader(LOAD_TYPES.SEARCH_RESULTS_LOAD_MORE)
@fixedHeightContent
@stateLogic({
  searchRelatedJobs,
  isHoldReasonsJobRunning,
  VALID_FLASH_VIEWS,
})
class SearchResultsInner extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = { shouldRender: false };
    this.isRandomizationBannerRef = false;
    this.isMigratedSearchBannerVisibleRef = false;
  }

  static getDerivedStateFromProps(nextProps: any) {
    if (nextProps.searchResults.isLoading) {
      return { shouldRender: true };
    }
    return null;
  }

  // Mount effect - dispatch actions after first render
  componentDidMount() {
    const {
      params,
      filteredFormValues,
      filteredQuery,
      searchResults,
      multiSelect,
    } = this.props;
    const searchId = params.id;

    this.props.setBreadcrumbForSearchesResultsPage();
    this.props.initTableConfig(searchId);
    this.props.loadSavedSearch({ searchId });
    multiSelect.reset();
    openSavedSearchStream(
      searchId,
      isEmpty(filteredFormValues) ? null : filteredQuery,
    );

    if (IfFeature.isDisabled('results-split-view')) {
      this.props.changeAppView(searchResults.appViews.SINGLE);
    }

    this.props.setRootComponentLeaveValidator(this.routerWillLeave);
  }

  componentWillUnmount() {
    this.props.resetCurrentSavedSearch();
    this.props.resetStream();
    this.props.updateDropdown('reset');
    this.props.multiSelect.reset();
  }

  componentDidUpdate(prevProps: any) {
    const { searchResults, multiSelect } = this.props;
    
    // Set multi-select result counts
    if (!searchResults.isLoading && prevProps.searchResults.isLoading) {
      multiSelect.setResultCounts(searchResults.returnCount);
    }

    // Handle sample search and bulk operations
    const { params, sampleSearchRunning } = this.props;
    const searchId = params.id;
    const isRawSearch = this.props.currentSavedSearch.search.raw;
    
    const previouslyRan = includes(prevProps.sampleSearchRunning, searchId);
    const sampleSearchRunningNow = includes(sampleSearchRunning, searchId);

    if (isRawSearch) {
      if (!this.isMigratedSearchBannerVisibleRef) {
        this.isMigratedSearchBannerVisibleRef = true;
        this.props.showGeneralFlash('migrated_search.information', VALID_FLASH_VIEWS);
      }
    } else {
      if (sampleSearchRunningNow && !this.isRandomizationBannerRef) {
        this.isRandomizationBannerRef = true;
        this.props.showGeneralFlash('random_search.running', VALID_FLASH_VIEWS);
      } else if (previouslyRan && !sampleSearchRunningNow) {
        this.props.resetFlash({ from: 'SearchResults/componentDidUpdate' });
        this.props.showSuccessFlash('random_search.create', VALID_FLASH_VIEWS);
      }

      this.props.checkBulkOperationStatuses(searchId);
    }
  }

  routerWillLeave = (nextRoute: any) => {
    const { searchResults, params } = this.props;
    
    if (!isSplitView({ searchResults })) {
      this.props.resetAssetStore();
    }

    if (nextRoute.pathname.includes(`results/${params.id}`)) {
      return { withoutReset: true };
    }

    return true;
  };

  get checkProps() {
    const { props } = this;
    const searchId = props.params.id;
    const isRawSearch = props.currentSavedSearch.search.raw;
    
    return {
      ...props,
      triedLoadingContext: props.triedLoadingContext || !isEmpty(props.filteredFormValues),
      searchId,
      isRawSearch,
    };
  }

  get renderContent() {
    const { searchResults } = this.props;
    const checkProps = this.checkProps;
    
    if (isSplitView({ searchResults })) {
      return (
        <React.Suspense fallback={<Loading />}>
          <SearchResultsSplitView
            {...this.props}
            userCanDeleteAssets={onDemandPurgeEnabledForAccount}
            withMultipleSelection
          />
        </React.Suspense>
      );
    }

    return (
      <div data-view="results-single-view">
        <React.Suspense fallback={<Loading />}>
          <Loaded
            {...checkProps}
            userCanDeleteAssets={onDemandPurgeEnabledForAccount}
          />
        </React.Suspense>
      </div>
    );
  }

  render() {
    const checkProps = this.checkProps;
    const { searchResults } = this.props;
    
    return (
      <data>
        <FlashMessages />
        <LoadWhenReady
          readyStates={[
            this.state.shouldRender,
            isReady(checkProps),
            searchResults.stream.id,
          ]}
        >
          {this.renderContent}
        </LoadWhenReady>
      </data>
    );
  }
}

SearchResultsInner.displayName = 'Searchresults.proxy';

// Connect to Redux
import { connect } from 'react-redux';

const mapStateToProps = ({
  currentSavedSearch,
  bulkActions,
  unsavedSearch,
  searchResults,
}: any) => ({
  bulkActions,
  currentSavedSearch,
  filteredFormValues: unsavedSearch.data.filteredFormValues,
  filteredQuery: unsavedSearch.data.query,
  searchResults,
  returnCount: searchResults.returnCount,
  sampleSearchRunning: searchResults.sampleSearchRunning,
});

const mapActionsToProps = {
  loadSavedSearch,
  resetCurrentSavedSearch,
  resetFlash,
  showSuccessFlash,
  showFailureFlash,
  showGeneralFlash,
  searchRelatedJobs,
  resetReviewJob,
  resetHoldReasonsjob,
  updateDropdown,
  loadSearchLists,
  resetAssetStore,
  setBreadcrumbForSearchesResultsPage: breadcrumbsActions.searchesResultsPage,
  changeAppView,
  initTableConfig,
  resetResults,
  resetStream,
};

const SearchResults = connect(
  mapStateToProps,
  mapActionsToProps,
)(SearchResultsInner);

export default SearchResults;
